<template>
  <div class="main">
    <b-card>
      <b-card-body>
        <b-row class="mb-2">
          <b-col md="12" class="d-flex justify-content-end">
            <!-- Button to trigger the modal -->
            <b-button variant="primary" @click="showAddDocumentModal">
              <feather-icon icon="PlusIcon" class="mr-1" />
              New Document
            </b-button>
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col md="2" sm="4" class="my-1">
            <b-form-group class="mb-0">
              <label class="d-inline-block text-sm-left mr-1">Per page</label>
              <b-form-select
                id="perPageSelect"
                v-model="perPage"
                size="sm"
                :options="pageOptions"
                class="w-75"
              />
            </b-form-group>
          </b-col>
          <!-- <b-col md="6" class="my-1">
            <b-form-group
              label="Filter"
              label-cols-sm="3"
              label-align-sm="right"
              label-size="sm"
              label-for="filterInput"
              class="mb-0"
            >
              <b-input-group size="sm">
                <b-form-input
                  id="filterInput"
                  v-model="filter"
                  type="search"
                  placeholder="Type to Search"
                />
                <b-input-group-append>
                  <b-button :disabled="!filter" @click="filter = ''">
                    Clear
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col> -->
        </b-row>

        <b-row>
          <b-col cols="12">
            <b-table
              striped
              hover
              responsive
              :per-page="perPage"
              :current-page="currentPage"
              :items="filteredDocs"
              :fields="documentFields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              :filter="filter"
              :filter-included-fields="filterOn"
              @filtered="onFiltered"
            >
              <template #cell(status)="data">
                <b-badge :variant="statusVariants[data.item.status]">
                  {{ statusText[data.item.status] }}
                </b-badge>
              </template>

              <template #cell(issueDate)="data">
                <span v-if="data.item.issueDate">
                  {{ formatissueDate(data.item.issueDate) }}
                </span>
                <span v-else>-</span>
              </template>

              <template #cell(expiryDate)="data">
                <b-badge
                  v-if="isExpired(data.item.expiryDate)"
                  variant="danger"
                >
                  {{ formatexpiryDate(data.item.expiryDate) }}
                </b-badge>
                <span v-else>
                  {{ formatexpiryDate(data.item.expiryDate) }}
                </span>
              </template>

              <template #cell(actions)="data">
                <b-dropdown
                  variant="link"
                  toggle-class="p-0"
                  no-caret
                  class="m-1"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  <b-dropdown-item @click="showViewDocumentModal(data.item)">
                    <feather-icon icon="EyeIcon" />
                    <span class="align-middle ml-1">View</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-b-modal.modalEditDocument
                    @click="showEditDocumentModal(data.item)"
                  >
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-1">Edit</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="confirmDeleteDocument(data.item)">
                    <feather-icon icon="TrashIcon" />
                    <span class="align-middle ml-1">Delete</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="downloadDocument(data.item)">
                    <feather-icon icon="DownloadIcon" />
                    <span class="align-middle ml-1">Download</span>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="my-0"
            />
            <b-form-group>
              Showing
              {{ totalRows > 0 ? perPage * (currentPage - 1) + 1 : 0 }}
              to
              {{
                totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0
              }}
              of
              {{ totalRows > 0 ? totalRows : 0 }}
              entries
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
    <b-modal
      ref="modalAddDocument"
      id="modal-add-document"
      title="Add New Document"
      centered
      hide-footer
    >
      <validation-observer
        ref="AddDocumentForm"
        v-slot="{ submitting, handleSubmit }"
      >
        <b-form @submit.prevent="handleSubmit(addNewDocument)">
          <b-row>
            <b-col cols="12">
              <b-form-group label="Name" label-for="documentName">
                <validation-provider
                  name="Document Name"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="documentName"
                    v-model="newDocument.documentName"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Description" label-for="documentDescription">
                <b-form-input
                  id="documentDescription"
                  v-model="newDocument.notes"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Type" label-for="documentType">
                <validation-provider
                  name="Document Type"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-select
                    id="documentType"
                    v-model="newDocument.documentType"
                    :options="documentType"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Issue Date" label-for="issueDate">
                <validation-provider name="Issue Date" v-slot="{ errors }">
                  <b-form-datepicker
                    id="issueDate"
                    v-model="newDocument.issueDate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Expiry Date" label-for="expiryDate">
                <validation-provider
                  name="Expiry Date"
                  :rules="{ required: false, dateAfter: newDocument.issueDate }"
                  v-slot="{ errors }"
                >
                  <b-form-datepicker
                    id="expiryDate"
                    v-model="newDocument.expiryDate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label="Notification Date"
                label-for="NotificationDate"
              >
                <b-form-datepicker
                  id="NotificationDate"
                  v-model="newDocument.NotificationDate"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Status" label-for="status">
                <validation-provider
                  name="Status"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-select
                    id="status"
                    v-model="newDocument.status"
                    :options="documentStatus"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <!-- File Upload Section -->
            <b-col cols="12">
              <b-form-group label="Upload File" label-for="documentFile">
                <validation-provider
                  name="Document File"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <div class="d-flex align-items-center">
                    <b-form-input
                      id="documentFile"
                      v-model="newDocument.filename"
                      class="mr-2"
                      readonly
                    />

                    <b-button variant="primary" @click="openMediaDialog"
                      >Browse</b-button
                    >
                    <input
                      type="file"
                      ref="mediaInput"
                      class="d-none"
                      @change="handleMediaUpload"
                    />
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <small v-if="fileSizeError" class="text-danger">{{
                  fileSizeError
                }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          <b-button
            type="submit"
            variant="primary"
            :disabled="submitting"
            @click="addNewDocument"
          >
            <b-spinner small v-if="submitting"></b-spinner>
            {{ submitting ? "Adding..." : "Add Document" }}
          </b-button>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- Modal for viewing a document -->
    <b-modal
      ref="modalViewDocument"
      title="View Document"
      cancel-variant="outline-secondary"
      hide-footer
      centered
    >
      <b-row>
        <b-form @submit.prevent>
          <b-row>
            <b-col cols="12">
              <b-form-group label="Name" label-for="viewDocumentName">
                <b-form-input
                  id="viewDocumentName"
                  v-model="viewDocumentData.documentName"
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label="Description"
                label-for="viewDocumentDescription"
              >
                <b-form-input
                  id="viewDocumentDescription"
                  v-model="viewDocumentData.notes"
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Type" label-for="viewDocumentType">
                <b-form-input
                  id="viewDocumentType"
                  v-model="viewDocumentData.documentType"
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Issue Date" label-for="viewIssueDate">
                <b-form-datepicker
                  id="viewIssueDate"
                  type="date"
                  v-model="viewDocumentData.issueDate"
                  disabled
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Expiry Date" label-for="viewExpiryDate">
                <b-form-datepicker
                  id="viewExpiryDate"
                  type="date"
                  v-model="viewDocumentData.expiryDate"
                  disabled
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label="Notification Date"
                label-for="viewNotificationDate"
              >
                <b-form-datepicker
                  id="viewNotificationDate"
                  type="date"
                  v-model="viewDocumentData.notificationDate"
                  disabled
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Status" label-for="viewStatus">
                <b-form-select
                  id="viewStatus"
                  v-model="viewDocumentData.status"
                  :options="documentStatus"
                  readonly
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-row>
    </b-modal>

    <!-- Modal for editing a document -->
    <b-modal
      id="modalEditDocument"
      title="Edit Document"
      cancel-variant="outline-secondary"
      hide-footer
      centered
    >
      <!-- <validation-observer ref="EditDocumentForm" v-slot="{ invalid, handleSubmit }"> -->
      <b-row>
        <b-form @submit.prevent="handleSubmit(updateDocument)">
          <b-row>
            <b-col cols="12">
              <b-form-group label="Name" label-for="editDocumentName">
                <b-form-input
                  id="editDocumentName"
                  v-model="editDocumentData.documentName"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label="Description"
                label-for="editDocumentDescription"
              >
                <b-form-input
                  id="editDocumentDescription"
                  v-model="editDocumentData.notes"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Type" label-for="editDocumentType">
                <b-form-select
                  id="editDocumentType"
                  v-model="editDocumentData.documentType"
                  :options="this.documentType"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Issue Date" label-for="editIssueDate">
                <b-form-datepicker
                  id="editIssueDate"
                  type="date"
                  v-model="editDocumentData.issueDate"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Expiry Date" label-for="expiryDate">
                <validation-provider
                  name="Expiry Date"
                  :rules="{
                    required: true,
                    dateAfter: editDocumentData.issueDate,
                  }"
                  v-slot="{ errors }"
                >
                  <b-form-datepicker
                    id="expiryDate"
                    v-model="editDocumentData.expiryDate"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label="Notification Date"
                label-for="NotificationDate"
              >
                <b-form-datepicker
                  id="NotificationDate"
                  v-model="editDocumentData.notificationDate"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Status" label-for="editStatus">
                <b-form-select
                  id="editStatus"
                  v-model="editDocumentData.status"
                  :options="documentStatus"
                  required
                />
              </b-form-group>
            </b-col>
            <!-- File Upload Section -->
            <!-- <b-col cols="12">
              <b-form-group label="Upload File" label-for="editDocumentFile">
                <div class="d-flex align-items-center">
                  <b-form-input
                    id="editDocumentFile"
                    v-model="editDocumentData.filename"
                    class="mr-2"
                    readonly
                  />
                  <b-button variant="primary" @click="openMediaDialog"
                    >Browse</b-button
                  >
                  <input
                    type="file"
                    ref="mediaInput"
                    class="d-none"
                    @change="handleMediaUpload"
                  />
                </div>
              </b-form-group>
            </b-col> -->
          </b-row>
          <!-- <b-button type="submit" variant="primary">Update Document</b-button> -->
          <button
            type="button"
            class="btn btn-primary"
            block
            :disabled="submitting"
            @click="updateDocument()"
          >
            Update Document
          </button>
        </b-form>
      </b-row>
      <!-- </validation-observer> -->
    </b-modal>
  </div>
</template>

<script>
import {
  BTable,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BModal,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BCard,
  BDropdown,
  BCardBody,
  BDropdownItem,
  BFormDatepicker,
} from "bootstrap-vue";
import Swal from "sweetalert2";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { mapActions, mapGetters } from "vuex";
import { customLoader, customError, customSwal } from "@/libs/customFn";
import {
  EmployeeDocumentType,
  EmployeeDocumentStatus,
} from "@/libs/defaultList";
import { extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";

// Extend the 'required' rule
extend("required", {
  ...required,
  message: "This field is required",
});

extend("dateAfter", {
  params: ["target"],
  validate(value, { target }) {
    if (!value || !target) return true;
    return new Date(value) > new Date(target);
  },
  message: "Expiry Date cannot be before Issue Date",
});

export default {
  components: {
    BCard,
    BTable,
    BBadge,
    BRow,
    BCardBody,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BModal,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BFormDatepicker,
  },

  props: {
    employeeDetail: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      id: this.$route.params.id,
      submitting: false,
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 1,
      currentPage: 1,
      filter: null,
      filterOn: ["name", "type", "issueDate", "expiryDate", "status"], // Adjust this based on your filter fields
      documentFields: [
        { key: "documentName", label: "Name", sortable: true },
        { key: "notes", label: "Description", sortable: true },
        { key: "documentType", label: "Type", sortable: true },
        { key: "issueDate", label: "Issue Date", sortable: true },
        { key: "expiryDate", label: "Expiry Date", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "actions", label: "Actions" },
      ],
      sortBy: null,
      sortDesc: false,

      sortDirection: "asc",
      documentStatus: EmployeeDocumentStatus,
      documentType: EmployeeDocumentType,
      fileSizeError: null,
      newDocument: {
        documentName: null,
        documentType: null,
        issueDate: null,
        expiryDate: null,
        NotificationDate: null,
        status: null,
        notes: null,
        filename: null,
      },

      viewDocumentData: {
        documentName: null,
        notes: null,
        documentType: null,
        issueDate: null,
        expiryDate: null,
        notificationDate: null,
        status: null,
        filename: null,
      },
      editDocumentData: {
        documentName: null,
        notes: null,
        documentType: null,
        issueDate: null,
        expiryDate: null,
        notificationDate: null,
        status: null,
        filename: null,
      },
      selectedFile: null,
      statusText: {
        0: "Pending",
        1: "Approved",
        2: "Rejected",
      },
      statusVariants: {
        0: "warning",
        1: "success",
        2: "danger",
      },
      docs: [],
    };
  },
  async mounted() {
    console.log("mountdocs", this.employeeDocs);
  },

  computed: {
    ...mapGetters("employeeModule", {
      employeeDocs: "employeeDocs",
      loading: "loading",
    }),

    filteredDocs() {
      let docs = this.employeeDocs;

      if (this.filter) {
        const filter = this.filter.toLowerCase();
        docs = this.employeeDocs.filter((doc) => {
          return (
            String(doc.documentName).toLowerCase().includes(filter) ||
            String(doc.documentType).toLowerCase().includes(filter)
          );
        });
      }

      //this.totalRows = docs.length;

      return docs;
    },
  },
  methods: {
    ...mapActions("employeeModule", [
      "getEmployeeIdDocAction",
      "updateEmployeeDocAction",
      "addEmployeeDocAction",
      "removeEmployeeDocAction",
      "downloadEmployeeDocAction",
    ]),
    refreshData() {},
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    formatissueDate(issueDate) {
      if (!issueDate) {
        return "-";
      }
      return new Date(issueDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatexpiryDate(expiryDate) {
      if (!expiryDate) {
        return "-";
      }
      return new Date(expiryDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    // Method to show the add document modal
    showAddDocumentModal() {
      this.$refs.modalAddDocument.show();
    },
    showViewDocumentModal(item) {
      this.viewDocumentData = item;
      console.log(this.viewDocumentData); // Check if data is populated correctly
      this.$refs.modalViewDocument.show();
    },

    showEditDocumentModal(item) {
      this.editDocumentData = item;
      this.$refs.modalEditDocument.show();
    },

    async addNewDocument() {
      this.submitting = true;

      const isValid = await this.$refs.AddDocumentForm.validate();

      if (this.fileSizeError) {
        this.submitting = false;
        return;
      }
      if (isValid) {
        const maxSize = 25 * 1024 * 1024;
        if (this.selectedFile && this.selectedFile.size > maxSize) {
          alert("File size exceeds 25 MB limit. Please choose a smaller file.");
          this.submitting = false;
          return;
        }

        // if (this.newDocument.documentName != this.selectedFile.name){
        //   alert('The document name does not match!');
        //   this.submitting = false;
        //   return;
        // }

        try {
          let docForm = new FormData();
          docForm.append("DocumentType", this.newDocument.documentType);
          docForm.append("DocumentName", this.newDocument.documentName);

          if (this.newDocument.issueDate)
            docForm.append("IssueDate", this.newDocument.issueDate);

          if (this.newDocument.expiryDate)
            docForm.append("ExpiryDate", this.newDocument.expiryDate);

          if (this.newDocument.NotificationDate)
            docForm.append(
              "NotificationDate",
              this.newDocument.NotificationDate
            );

          docForm.append("Status", this.newDocument.status);
          docForm.append("Notes", this.newDocument.notes);
          docForm.append("EmployeeId", this.employeeDetail.id);
          docForm.append("File", this.selectedFile);
          console.log("testign1", this.selectedFile);

          this.addDocLoader = customLoader(
            "Please wait while document is being uploaded"
          );

          this.addEmployeeDocAction(docForm)
            .then((response) => {
              if (response.success) {
                customSwal("Document has been uploaded", "Success", "success");
                this.newDocument.id = response.result.id;
                //this.$router.push({ path: 'employee-listing' });
              } else {
                customError(response, "Error");
              }
              this.addDocLoader.close();
            })
            .catch((error) => {
              this.addDocLoader.close();
              customError(error.response.data, "Error");
            });

          this.newDocument.id = this.employeeDocs.length
            ? this.employeeDocs[this.employeeDocs.length - 1].id + 1
            : 1;
          //  this.employeeDocs.push({ ...this.newDocument });
        } catch (error) {
          this.error(error.response.data);
        } finally {
          this.submitting = false;
          this.$refs.modalAddDocument.hide();
          // Optionally, reset the form fields after adding the document
          this.resetForm();
          this.getEmployeeIdDocAction(this.id);

          // this.filteredDocs();
        }
      } else {
        this.submitting = false;
      }
    },
    // Method to edit a document
    editDocument(document) {
      this.editDocumentData = document;
      //this.showEditModal = true;
    },
    // Method to update an existing document
    updateDocument() {
      const index = this.employeeDocs.findIndex(
        (doc) => doc.id === this.editDocumentData.id
      );

      const maxSize = 25 * 1024 * 1024;
      if (this.selectedFile && this.selectedFile.size > maxSize) {
        alert("File size exceeds 25 MB limit. Please choose a smaller file.");
        this.submitting = false;
        return;
      }

      let docForm = new FormData();
      docForm.append("Id", this.editDocumentData.id);
      docForm.append("DocumentType", this.editDocumentData.documentType);
      docForm.append("DocumentName", this.editDocumentData.documentName);

      if (this.editDocumentData.issueDate)
        docForm.append("IssueDate", this.editDocumentData.issueDate);

      if (this.editDocumentData.expiryDate)
        docForm.append("ExpiryDate", this.editDocumentData.expiryDate);

      if (this.editDocumentData.notificationDate)
        docForm.append(
          "NotificationDate",
          this.editDocumentData.notificationDate
        );

      docForm.append("Status", this.editDocumentData.status);
      docForm.append("Notes", this.editDocumentData.notes);
      docForm.append("EmployeeId", this.employeeDetail.id);
      docForm.append("File", this.selectedFile);
      this.editDocLoader = customLoader(
        "Please wait while document is being uploaded"
      );

      this.updateEmployeeDocAction(docForm)
        .then((response) => {
          if (response.success) {
            customSwal("Document has been updated", "Success", "success");

            this.getEmployeeIdDocAction(this.id);
            if (index !== -1) {
              this.employeeDocs.splice(index, 1, { ...this.editDocumentData });
            }
          } else {
            customError(response, "Error");
          }
          this.editDocLoader.close();
        })
        .catch((error) => {
          this.editDocLoader.close();
          customError(error.response.data, "Error");
        });

      this.filteredDocs();
      if (index !== -1) {
        this.$refs.modalEditDocument.hide();
        this.resetForm();
      }
    },
    // Method to reset form fields after adding a document
    resetForm() {
      this.newDocument = {
        documentName: null,
        notes: null,
        documentType: null,
        issueDate: null,
        expiryDate: null,
        NotificationDate: null,
        status: null,
        filename: null,
      };
      this.viewDocumentData = {
        documentName: null,
        notes: null,
        documentType: null,
        issueDate: null,
        expiryDate: null,
        status: null,
        filename: null,
      };
      this.editDocumentData = {
        documentName: null,
        notes: null,
        documentType: null,
        issueDate: null,
        expiryDate: null,
        status: null,
        filename: null,
      };
    },

    openMediaDialog() {
      this.$refs.mediaInput.click();
    },
    handleMediaUpload(event) {
      const file = event.target.files[0];
      console.log(file);
      if (file) {
        // Check file size (20MB limit)
        const maxSize = 20 * 1024 * 1024; // 20MB in bytes
        if (file.size > maxSize) {
          this.fileSizeError = "File size exceeds the limit of 20MB.";
          this.selectedFile = null; // Reset selected file
          this.newDocument.filename = null; // Reset filename display
        } else {
          this.fileSizeError = null; // Clear any previous error
          this.selectedFile = file;
          this.newDocument.filename = file.name;
          //this.newDocument.documentName = file.name;
          this.editDocumentData.filename = file.name;
          //this.editDocumentData.documentName = file.name;
        }
      }
    },

    confirmDeleteDocument(document) {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteDocument(document);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          Swal.fire({
            title: "Cancelled",
            text: "Your document is safe :)",
            icon: "error",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
        }
      });
    },
    deleteDocument(document) {
      this.deleteDocLoader = customLoader(
        "Please wait while document is being removed"
      );
      this.removeEmployeeDocAction(document.id)
        .then((result) => {
          this.deleteDocLoader.close();
          customSwal("Document has been removed", "Success");
          const index = this.employeeDocs.findIndex(
            (doc) => doc.id === document.id
          );
          if (index !== -1) this.employeeDocs.splice(index, 1);
        })
        .catch((error) => {
          this.deleteDocLoader.close();
          customError(error.response.data, "Error");
        });
    },

    extractFilenameFromDisposition(disposition) {
      if (!disposition) return null;
      const match = disposition.match(
        /filename\*?=(?:UTF-8'')?["']?([^;"']+)/i
      );
      return match ? decodeURIComponent(match[1]) : null;
    },

    downloadDocument(doc) {
      this.downloadDocLoader = customLoader(
        "Please wait while document is being downloaded"
      );
      this.downloadEmployeeDocAction(doc.id)
        .then((response) => {
          this.downloadDocLoader.close();

          if (response) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", doc.documentName);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(url);
          } else {
            customError(response, "Error");
          }
        })
        .catch((error) => {
          this.downloadDocLoader.close();
          customError(error.response?.data || error.message, "Error");
        });
    },

    isExpired(expiryDate) {
      const today = new Date();
      const expiry = new Date(expiryDate);
      return expiry < today;
    },
  },
};
</script>